import React, { useState, useEffect, useRef, useCallback } from "react";

import chatDotsFill from "@iconify/icons-bi/chat-dots-fill";
import { Icon } from "@iconify/react/dist/offline";
import { useLocation } from "react-router-dom";

import { Button } from "src/components/ui/button";
import { cn } from "src/lib/utils";

import useUser from "./hooks/useUser";

const DRAG_THRESHOLD = 5;

const CustomIntercomLauncher = () => {
  const { isStaff } = useUser();
  const initialPosition = { x: 20, y: 30 };

  const [position, setPosition] = useState(initialPosition);
  const [dragging, setDragging] = useState(false);
  const didDragRef = useRef(false);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      didDragRef.current = false;

      const startX = e.clientX;
      const startY = e.clientY;
      const relX = window.innerWidth - e.clientX - position.x;
      const relY = window.innerHeight - e.clientY - position.y;

      const onMove = (me: MouseEvent) => {
        const dx = me.clientX - startX;
        const dy = me.clientY - startY;
        if (!didDragRef.current && Math.sqrt(dx * dx + dy * dy) > DRAG_THRESHOLD) {
          didDragRef.current = true;
          setDragging(true);
        }
        if (didDragRef.current) {
          setPosition({
            x: window.innerWidth - me.clientX - relX,
            y: window.innerHeight - me.clientY - relY,
          });
        }
      };

      const onUp = () => {
        setDragging(false);
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);

        // If it was a click (no drag), trigger Intercom
        if (!didDragRef.current) {
          if (window.Intercom) {
            window.Intercom("show");
          }
        }
      };

      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    [position]
  );

  const location = useLocation();

  useEffect(() => {
    setPosition(initialPosition);
  }, [location.pathname]);

  if (isStaff) return null;
  return (
    <div
      onMouseDown={handleMouseDown}
      style={{
        position: "fixed",
        right: position.x,
        bottom: position.y,
        zIndex: 5000,
        padding: "8px",
      }}
      title="Drag to move, click to chat"
    >
      <Button
        variant="outline"
        size="sm"
        className={cn(
          "shadow-lg bg-background",
          dragging ? "cursor-move" : "cursor-pointer"
        )}
      >
        <Icon icon={chatDotsFill} width={16} height={16} />
        Chat with us
      </Button>
    </div>
  );
};

export default CustomIntercomLauncher;
